import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, Phone, Mail, MapPin, Clock, Filter, Search,
  ArrowLeft, RefreshCw, Check, X, MessageCircle, Send,
  TrendingUp, Eye, Calendar, ChevronDown, BarChart3,
  Building, IndianRupee, Star, AlertCircle
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const LeadDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [referralLeads, setReferralLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-leads');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leadsRes, referralRes] = await Promise.all([
        api.get('/leads/my-leads'),
        api.get('/leads/my-referral-leads')
      ]);
      
      setLeads(leadsRes.data.leads || []);
      setReferralLeads(referralRes.data.leads || []);
      
      // Calculate stats
      const allLeads = leadsRes.data.leads || [];
      setStats({
        total: allLeads.length,
        new: allLeads.filter(l => l.status === 'new').length,
        contacted: allLeads.filter(l => l.status === 'contacted').length,
        interested: allLeads.filter(l => l.status === 'interested').length,
        converted: allLeads.filter(l => l.status === 'converted').length,
        closed: allLeads.filter(l => l.status === 'closed').length,
        referralTotal: referralRes.data.total || 0
      });
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus, notes = '') => {
    setUpdatingStatus(true);
    try {
      await api.put(`/api/leads/update-status/${leadId}?status=${newStatus}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`);
      toast.success('Lead status updated');
      fetchData();
      setSelectedLead(null);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const sendWhatsApp = (lead) => {
    const message = `Hi ${lead.name}, Thank you for your interest in the property "${lead.listing_title}". I would love to discuss more details with you. When would be a good time to talk?`;
    window.open(`https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const callLead = (phone) => {
    window.open(`tel:${phone}`, '_self');
  };

  const filteredLeads = (activeTab === 'my-leads' ? leads : referralLeads)
    .filter(lead => statusFilter === 'all' || lead.status === statusFilter)
    .filter(lead => 
      searchTerm === '' ||
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone?.includes(searchTerm) ||
      lead.listing_title?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const statusColors = {
    new: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
    contacted: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
    interested: 'bg-purple-500/20 text-purple-500 border-purple-500/30',
    converted: 'bg-green-500/20 text-green-500 border-green-500/30',
    closed: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };

  const statusOptions = [
    { value: 'new', label: 'New', icon: Star },
    { value: 'contacted', label: 'Contacted', icon: Phone },
    { value: 'interested', label: 'Interested', icon: Eye },
    { value: 'converted', label: 'Converted', icon: Check },
    { value: 'closed', label: 'Closed', icon: X }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="lead-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500/20 via-background to-blue-500/10 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Lead Dashboard</h1>
                <p className="text-muted-foreground">Manage your property inquiries</p>
              </div>
            </div>
            <button
              onClick={fetchData}
              className="px-4 py-2 bg-white/10 rounded-xl flex items-center gap-2 hover:bg-white/20"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 -mt-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="glass-card p-4 text-center border-t-4 border-blue-500">
            <p className="text-2xl font-bold text-blue-500">{stats?.total || 0}</p>
            <p className="text-xs text-muted-foreground">Total Leads</p>
          </div>
          <div className="glass-card p-4 text-center border-t-4 border-yellow-500">
            <p className="text-2xl font-bold text-yellow-500">{stats?.new || 0}</p>
            <p className="text-xs text-muted-foreground">New</p>
          </div>
          <div className="glass-card p-4 text-center border-t-4 border-purple-500">
            <p className="text-2xl font-bold text-purple-500">{stats?.contacted || 0}</p>
            <p className="text-xs text-muted-foreground">Contacted</p>
          </div>
          <div className="glass-card p-4 text-center border-t-4 border-cyan-500">
            <p className="text-2xl font-bold text-cyan-500">{stats?.interested || 0}</p>
            <p className="text-xs text-muted-foreground">Interested</p>
          </div>
          <div className="glass-card p-4 text-center border-t-4 border-green-500">
            <p className="text-2xl font-bold text-green-500">{stats?.converted || 0}</p>
            <p className="text-xs text-muted-foreground">Converted</p>
          </div>
          <div className="glass-card p-4 text-center border-t-4 border-primary">
            <p className="text-2xl font-bold text-primary">{stats?.referralTotal || 0}</p>
            <p className="text-xs text-muted-foreground">From Referrals</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 mt-8">
        <div className="flex gap-2 border-b border-border">
          <button
            onClick={() => setActiveTab('my-leads')}
            className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === 'my-leads' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-white'
            }`}
          >
            <Building className="w-4 h-4" />
            My Property Leads ({leads.length})
          </button>
          <button
            onClick={() => setActiveTab('referral-leads')}
            className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === 'referral-leads' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-white'
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            Referral Leads ({referralLeads.length})
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 mt-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name, phone, or property..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-xl"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-white/5 border border-white/10 rounded-xl"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="interested">Interested</option>
              <option value="converted">Converted</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Leads List */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {filteredLeads.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">No leads yet</h3>
            <p className="text-muted-foreground">
              {activeTab === 'my-leads' 
                ? 'Leads will appear here when someone inquires about your properties.'
                : 'Referral leads will appear when someone visits properties via your referral link.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredLeads.map(lead => (
              <div 
                key={lead.id} 
                className="glass-card p-4 hover:border-primary/30 transition-colors"
                data-testid={`lead-card-${lead.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Lead Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-lg">{lead.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs border ${statusColors[lead.status]}`}>
                        {lead.status}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      <Building className="w-4 h-4 inline mr-1" />
                      {lead.listing_title || 'Property Inquiry'}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4 text-green-500" />
                        {lead.phone}
                      </span>
                      {lead.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4 text-blue-500" />
                          {lead.email}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        {new Date(lead.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {lead.interested_in && (
                      <p className="text-sm mt-2">
                        <span className="text-muted-foreground">Interest:</span>{' '}
                        <span className="capitalize">{lead.interested_in}</span>
                        {lead.budget_range && ` • Budget: ${lead.budget_range}`}
                      </p>
                    )}
                    {lead.message && (
                      <p className="text-sm text-muted-foreground mt-2 italic">
                        "{lead.message}"
                      </p>
                    )}
                    {lead.referrer_username && (
                      <p className="text-xs text-primary mt-2">
                        Referred by: {lead.referrer_username}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => sendWhatsApp(lead)}
                      className="px-3 py-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30 flex items-center gap-1"
                    >
                      <MessageCircle className="w-4 h-4" />
                      WhatsApp
                    </button>
                    <button
                      onClick={() => callLead(lead.phone)}
                      className="px-3 py-2 bg-blue-500/20 text-blue-500 rounded-lg hover:bg-blue-500/30 flex items-center gap-1"
                    >
                      <Phone className="w-4 h-4" />
                      Call
                    </button>
                    {activeTab === 'my-leads' && (
                      <button
                        onClick={() => setSelectedLead(lead)}
                        className="px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 flex items-center gap-1"
                      >
                        <ChevronDown className="w-4 h-4" />
                        Update
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Update Status Modal */}
      {selectedLead && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedLead(null)}
        >
          <div 
            className="glass-card p-6 max-w-md w-full"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4">Update Lead Status</h3>
            <p className="text-muted-foreground mb-4">
              Lead: <span className="text-white font-medium">{selectedLead.name}</span>
            </p>
            
            <div className="grid grid-cols-2 gap-3 mb-6">
              {statusOptions.map(status => (
                <button
                  key={status.value}
                  onClick={() => updateLeadStatus(selectedLead.id, status.value)}
                  disabled={updatingStatus}
                  className={`p-3 rounded-xl border flex items-center gap-2 transition-colors ${
                    selectedLead.status === status.value
                      ? statusColors[status.value]
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <status.icon className="w-4 h-4" />
                  {status.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => setSelectedLead(null)}
              className="w-full py-2 text-muted-foreground hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDashboard;
